import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Permission } from '@/features/rbac/permissions';
import { useAuth } from './AuthProvider';

/** Gate a route behind an authenticated Supabase session. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { ready, user, previewMode } = useAuth();
  const location = useLocation();
  if (previewMode) return <>{children}</>; // dev preview without Supabase
  if (!ready) return <RouteSpinner />;
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}

/** Gate a route behind staff/owner membership. */
export function RequireRole({
  children,
  staff = false,
  owner = false,
}: {
  children: ReactNode;
  staff?: boolean;
  owner?: boolean;
}) {
  const { ready, user, isStaff, isOwner, previewMode } = useAuth();
  const location = useLocation();
  if (previewMode) return <>{children}</>;
  if (!ready) return <RouteSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (owner && !isOwner) return <Navigate to="/" replace />;
  if (staff && !isStaff) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Gate UI behind a specific permission (used inside the consoles). */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can, previewMode } = useAuth();
  if (previewMode || can(permission)) return <>{children}</>;
  return <>{fallback}</>;
}

function RouteSpinner() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
      <span className="animate-pulse">Loading…</span>
    </div>
  );
}
